import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import useReactApexChart from '../hook/useReactApexChart';
import ReactApexChart from 'react-apexcharts';
import "./styles/nalsuvaiDashboardLayer.css";
import dashboardApi from '../apiProvider/dashboardApi';
import currencyApi from '../apiProvider/currencyapi';
import { IMAGE_BASE_URL } from '../network/apiClient';

const initials = (n) => (n || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const AV_COLORS = ['#f6993f', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#1a2238'];

const formatCurrency = (amount, currencyDetails) => {
    const symbol = currencyDetails?.symbol;
    const code = currencyDetails?.code;
    return `${symbol}${Number(amount || 0).toLocaleString()}`;
};

export default function NalsuvaiDashboardLayer() {
    const [newCustomer, setNewCustomers] = useState([]);
    const [overviewPeriod, setOverviewPeriod] = useState('Yearly');
    const [topSellingProducts, setTopSellingProducts] = useState({ data: [], currencyDetails: { symbol: '', code: '' } });
    const [overAllSales, setOverAllSales] = useState({ sales: [], currencyDetails: { symbol: '', code: '' } });
    const [overview, setOverview] = useState({
        totalSales: { value: 0, percentage: 0 },
        totalOrders: { value: 0, percentage: 0 },
        totalProductsSold: { value: 0, percentage: 0 },
        newCustomers: { value: 0, percentage: 0 },
        currencyDetails: { symbol: '', code: '' }
    });
    const [invoices, setInvoices] = useState([]);
    const [orderStatusCount, setOrderStatusCount] = useState([]);
    const [totalOrder, setTotalOrder] = useState(0)
    const [categoryByRevenue, setCategoryByRevenue] = useState({ categories: [], totalRevenue: 0, currencyDetails: { symbol: '', code: '' } });
    const [lowStock, setLowStock] = useState([]);
    const [topCustomers, setTopCustomers] = useState({ topCustomers: [], currencyDetails: { symbol: '', code: '' } });
    const [ordersByCurrency, setOrdersByCurrency] = useState([]);

    const [currencies, setCurrencies] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);



    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData = months.map(m => {
        const f = overAllSales?.sales?.find(i => i.month === m);
        return {
            month: m,
            sales: f ? f.totalSalesAmount : 0,
            orders: f ? f.totalOrders : 0,
            customers: f ? f.totalCustomers : 0
        };
    });

    const peakMonth = monthlyData.reduce((a, b) => b.sales > a.sales ? b : a, monthlyData[0]);
    const totalSales = monthlyData.reduce((a, b) => a + b.sales, 0);

    /* ── API ── */
    useEffect(() => {
        if (!selectedCurrency) return;
        fetchLatestCustomer();
        fetchTopSellingProducts();
        fetchOverAllSales();
        fetchOverview();
        fetchInvoice();
        fetchOrderStatusCount();
        fetchCategoryByRevenue();
        fetchLowStock();
        fetchTopCustomers();
        fetchOrdersByCurrency();
    }, [overviewPeriod, selectedCurrency]);

    useEffect(() => {
        fetchCurrencies();
    }, []);

    const fetchCurrencies = async () => {
        try {
            const r = await currencyApi.getCurrencies({ limit: 1000 });
            if (r?.status) {
                const list = r.response?.data || [];
                setCurrencies(list);
                const defaultCurr = list.find(c => c.isDefault);
                if (defaultCurr) {
                    setSelectedCurrency(defaultCurr._id);
                } else if (list.length > 0) {
                    setSelectedCurrency(list[0]._id);
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchLatestCustomer = async () => {
        try {
            const r = await dashboardApi.getLatestCustomer();
            if (r?.status) setNewCustomers(r.response.data?.slice(0, 5) || []);
        } catch (error) {
            console.error(error);
        }
    }

    const fetchTopSellingProducts = async () => {
        try {
            const r = await dashboardApi.getTopSelling(overviewPeriod, selectedCurrency);
            if (r?.status) {
                const data = r.response || { data: [] };
                const curr = data.data?.[0]?.currencyDetails || { symbol: '', code: '' };
                setTopSellingProducts({ ...data, currencyDetails: curr });
            }
        } catch (error) {
            console.error(error)
        }
    }

    const fetchOverAllSales = async () => {
        try {
            const r = await dashboardApi.getOverAllSales(selectedCurrency);
            if (r?.status) setOverAllSales(r.response.data || { sales: [], currencyDetails: { symbol: '', code: '' } });
        } catch (error) {
            console.error(error)
        }
    }

    const fetchOverview = async () => {
        try {
            const r = await dashboardApi.getOverview(overviewPeriod, selectedCurrency);
            if (r?.status) setOverview(r.response.data || {
                totalSales: { value: 0, percentage: 0 },
                totalOrders: { value: 0, percentage: 0 },
                totalProductsSold: { value: 0, percentage: 0 },
                newCustomers: { value: 0, percentage: 0 },
                currencyDetails: { symbol: '', code: '' }
            });
        } catch (error) {
            console.error(error)
        }
    }
    const fetchInvoice = async () => {
        try {
            const r = await dashboardApi.getInvoice();
            if (r?.status) setInvoices(r.response.data.map(i => ({
                id: i.orderId,
                name: i.customerName || 'Unknown',
                date: i.date,
                status: i.paymentStatus,
            })));
        } catch (error) {
            console.error(error)
        }

    }

    const fetchOrderStatusCount = async () => {
        try {
            const r = await dashboardApi.getOrderStatusCount(overviewPeriod);
            if (r?.status) {

                setOrderStatusCount(r.response.data?.statusCounts)
                setTotalOrder(r.response.data?.totalOrder)
            }
        } catch (error) {
            console.error(error)
        }
    }

    const fetchCategoryByRevenue = async () => {

        try {
            const r = await dashboardApi.getCategoryByRevenue(overviewPeriod, selectedCurrency);
            if (r?.status) {
                setCategoryByRevenue(r.response?.data || { categories: [], totalRevenue: 0, currencyDetails: { symbol: '', code: '' } })
            }
        } catch (error) {
            console.error(error)
        }
    }

    const fetchLowStock = async () => {

        try {
            const r = await dashboardApi.getLowStock();
            if (r?.status) {
                setLowStock(r.response?.data || [])
            }
        } catch (error) {
            console.error(error)
        }
    }

    const fetchTopCustomers = async () => {
        try {
            const r = await dashboardApi.getTopCustomers(overviewPeriod, selectedCurrency);
            if (r?.status) {
                console.log(r.response.data, "getTopCustomers response.data");
                setTopCustomers(r.response?.data || { topCustomers: [], currencyDetails: { symbol: '', code: '' } })
            }
        } catch (error) {
            console.error(error)
        }
    }

    const fetchOrdersByCurrency = async () => {
        try {
            const r = await dashboardApi.getOrdersByCurrency(overviewPeriod);
            // Handling the specific response structure provided by the user
            if (r?.status === 'success') {
                setOrdersByCurrency(r.data || []);
            }
        } catch (error) {
            console.error("Error fetching orders by currency:", error);
        }
    }

    const { columnChartOptionsOne } = useReactApexChart();


    return (
        <div className="nd-root">

            {/* ── 1 · KPI ── */}
            <div className="d-flex align-items-center flex-wrap gap-3 justify-content-between mb-24">
                <h5 className="mb-0 mt-0 fw-semibold">DashBoard</h5>
                <div className="d-flex align-items-center gap-2">

                    <div className="nd-dropdown-container" style={{ position: 'relative', minWidth: '100px' }}>
                        {isCurrencyOpen && (
                            <div className="nd-dropdown-backdrop" onClick={() => setIsCurrencyOpen(false)} />
                        )}

                        <div
                            className="nd-dropdown-toggle radius-8 px-12 py-9 fw-medium d-flex align-items-center justify-content-between"
                            style={{
                                border: '1px solid var(--primary-600)',
                                color: 'var(--primary-600)',
                                background: 'var(--base)',
                                cursor: 'pointer',
                                minHeight: '42px',
                                userSelect: 'none',
                                position: 'relative',
                                zIndex: isCurrencyOpen ? 1051 : 1
                            }}
                            onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                        >
                            <span className="me-2">
                                {currencies.find(c => c._id === selectedCurrency) ? (
                                    `${currencies.find(c => c._id === selectedCurrency).code} ${currencies.find(c => c._id === selectedCurrency).symbol}`
                                ) : (
                                    'Select'
                                )}
                            </span>
                            <Icon icon="lucide:chevron-down" style={{ fontSize: '12px', transition: 'transform 0.3s', transform: isCurrencyOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                        </div>

                        <div className={`nd-dropdown-menu ${isCurrencyOpen ? 'show' : ''}`}>
                            {currencies.length > 0 ? (
                                currencies.map(c => (
                                    <div
                                        key={c._id}
                                        className="nd-dropdown-item"
                                        onClick={() => {
                                            setSelectedCurrency(c._id);
                                            setIsCurrencyOpen(false);
                                        }}
                                    >
                                        {c.code} {c.symbol}
                                    </div>
                                ))
                            ) : (
                                <div className="nd-dropdown-item fs-sm text-secondary-light">No option</div>
                            )}
                        </div>
                    </div>

                    {/* Vertical Separator */}
                    <div className="border-start h-24-px mx-8 border-neutral-300"></div>

                    <div className="d-flex align-items-center gap-2">
                        {['Yearly', 'Monthly', 'Weekly', 'Today'].map(p => (
                            <button
                                key={p}
                                className={`btn btn-sm ${overviewPeriod === p ? 'btn-primary' : 'btn-outline-primary'} radius-8 px-12`}
                                onClick={() => setOverviewPeriod(p)}
                            >
                                {p}
                            </button>
                        ))}
                    </div>



                </div>
            </div>

            <div className="row gy-4 gx-4 ">
                {/* Total Revenue */}
                <div className="col-sm-6">
                    <div className="nd-card nd-kpi-card sales">
                        <div className="card-body p-0">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                                <div className="d-flex align-items-center">
                                    <div className="w-64-px h-64-px radius-16 d-flex justify-content-center align-items-center me-20">
                                        <span className="w-40-px h-40-px bg-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center radius-8 h6 mb-0">
                                            <Icon icon="solar:bill-list-bold" className="icon text-2xl" />
                                        </span>
                                    </div>
                                    <div>
                                        <span className="mb-2 fw-medium text-secondary-light text-md">Total Revenue</span>
                                        <h6 className="fw-semibold my-1">{formatCurrency(overview.totalSales.value, overview.currencyDetails)}</h6>
                                        {/* <p className="text-sm mb-0">
                                            <span className={`${overview.totalSales.percentage >= 0 ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} px-1 rounded-2 fw-medium text-sm`}>
                                                {overview.totalSales.percentage >= 0 ? '+' : ''}{overview.totalSales.percentage}%
                                            </span>{" "}
                                            Gross revenue
                                        </p> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Total Orders */}
                <div className="col-sm-6">
                    <div className="nd-card nd-kpi-card orders">
                        <div className="card-body p-0">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                                <div className="d-flex align-items-center">
                                    <div className="w-64-px h-64-px radius-16 d-flex justify-content-center align-items-center me-20">
                                        <span className="w-40-px h-40-px bg-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center radius-8 h6 mb-0">
                                            <Icon icon="solar:cart-large-2-bold" className="icon text-2xl" />
                                        </span>
                                    </div>
                                    <div>
                                        <span className="mb-2 fw-medium text-secondary-light text-md">Total Orders</span>
                                        <h6 className="fw-semibold my-1">{overview.totalOrders.value.toLocaleString('en-IN')}</h6>
                                        {/* <p className="text-sm mb-0">
                                            <span className={`${overview.totalOrders.percentage >= 0 ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} px-1 rounded-2 fw-medium text-sm`}>
                                                {overview.totalOrders.percentage >= 0 ? '+' : ''}{overview.totalOrders.percentage}%
                                            </span>{" "}
                                            All orders
                                        </p> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Products Sold */}
                <div className="col-sm-6">
                    <div className="nd-card nd-kpi-card products">
                        <div className="card-body p-0">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                                <div className="d-flex align-items-center">
                                    <div className="w-64-px h-64-px radius-16 d-flex justify-content-center align-items-center me-20">
                                        <span className="w-40-px h-40-px bg-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center radius-8 h6 mb-0">
                                            <Icon icon="solar:box-bold" className="icon text-2xl" />
                                        </span>
                                    </div>
                                    <div>
                                        <span className="mb-2 fw-medium text-secondary-light text-md">Products Sold</span>
                                        <h6 className="fw-semibold my-1">{overview.totalProductsSold.value.toLocaleString('en-IN')}</h6>
                                        {/* <p className="text-sm mb-0">
                                            <span className={`${overview.totalProductsSold.percentage >= 0 ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} px-1 rounded-2 fw-medium text-sm`}>
                                                {overview.totalProductsSold.percentage >= 0 ? '+' : ''}{overview.totalProductsSold.percentage}%
                                            </span>{" "}
                                            Units out
                                        </p> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* New Customers */}
                <div className="col-sm-6">
                    <div className="nd-card nd-kpi-card customers">
                        <div className="card-body p-0">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                                <div className="d-flex align-items-center">
                                    <div className="w-64-px h-64-px radius-16 d-flex justify-content-center align-items-center me-20">
                                        <span className="w-40-px h-40-px bg-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center radius-8 h6 mb-0">
                                            <Icon icon="solar:users-group-rounded-bold" className="icon text-2xl" />
                                        </span>
                                    </div>
                                    <div>
                                        <span className="mb-2 fw-medium text-secondary-light text-md">New Customers</span>
                                        <h6 className="fw-semibold my-1">{overview.newCustomers.value.toLocaleString('en-IN')}</h6>
                                        {/* <p className="text-sm mb-0">
                                            <span className={`${overview.newCustomers.percentage >= 0 ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} px-1 rounded-2 fw-medium text-sm`}>
                                                {overview.newCustomers.percentage >= 0 ? '+' : ''}{overview.newCustomers.percentage}%
                                            </span>{" "}
                                            First-timers
                                        </p> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── 2 · SALES + STATUS ── */}
            <div className="row gy-4 mb-24 mt-12">
                <div className="col-xl-8">
                    <div className="card nd-card h-100 radius-12">
                        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                            <h6 className="text-lg fw-semibold mb-0">Sales Overview</h6>
                            {/* <div className="text-secondary-light text-sm">Revenue in ₹ thousands</div> */}
                        </div>
                        <div className="card-body p-24">
                            <div className="d-flex align-items-center flex-wrap gap-24 mb-24">
                                <div className="p-16 radius-12 bg-primary-50 border border-primary-100 d-flex align-items-center gap-12">
                                    <div className="w-40-px h-40-px radius-circle bg-primary-600 d-flex align-items-center justify-content-center text-white">
                                        <Icon icon="lucide:trending-up" className="text-lg" />
                                    </div>
                                    <div>
                                        <p className="text-secondary-light text-xs fw-medium mb-0" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Sales</p>
                                        <h6 className="mb-0 fw-bold text-primary-light">{formatCurrency(totalSales, overAllSales.currencyDetails)}</h6>
                                    </div>
                                </div>
                                <div className="p-16 radius-12 bg-success-50 border border-success-100 d-flex align-items-center gap-12">
                                    <div className="w-40-px h-40-px radius-circle bg-success-main d-flex align-items-center justify-content-center text-white">
                                        <Icon icon="lucide:award" className="text-lg" />
                                    </div>
                                    <div>
                                        <p className="text-secondary-light text-xs fw-medium mb-0" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>Peak Sales</p>
                                        <h6 className="mb-0 fw-bold text-success-main">{peakMonth?.month} <span className="text-xs fw-normal mx-1">·</span> {formatCurrency(peakMonth?.sales, overAllSales.currencyDetails)}</h6>
                                    </div>
                                </div>
                            </div>
                            <div id="monthly-sales-chart">
                                <ReactApexChart
                                    options={{
                                        ...columnChartOptionsOne,
                                        chart: { ...columnChartOptionsOne?.chart, toolbar: { show: false } },
                                        xaxis: { ...columnChartOptionsOne?.xaxis, categories: months },
                                        yaxis: {
                                            ...columnChartOptionsOne?.yaxis,
                                            labels: {
                                                formatter: (val) => formatCurrency(val, overAllSales.currencyDetails)
                                            }
                                        },
                                        tooltip: {
                                            enabled: true,
                                            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                                                const data = monthlyData[dataPointIndex];
                                                return `
                                                    <div class="px-3 py-2 bg-white shadow-sm border rounded-2" style="font-size: 13px;">
                                                        <div class="fw-bold mb-1 text-primary-light border-bottom pb-1">${data.month} Overview</div>
                                                        <div class="d-flex align-items-center justify-content-between gap-4 mb-1">
                                                            <span class="text-secondary-light">Sales:</span>
                                                            <span class="fw-semibold text-primary-main">${formatCurrency(data.sales, overAllSales.currencyDetails)}</span>
                                                        </div>
                                                        <div class="d-flex align-items-center justify-content-between gap-4">
                                                            <span class="text-secondary-light">Orders:</span>
                                                            <span class="fw-semibold text-primary-main">${data.orders.toLocaleString()}</span>
                                                        </div>
                                                        <div class="d-flex align-items-center justify-content-between gap-4">
                                                            <span class="text-secondary-light">Customers:</span>
                                                            <span class="fw-semibold text-primary-main">${data.customers}</span>
                                                        </div>
                                                    </div>
                                                `;
                                            }
                                        },
                                        colors: ['#1a2238'], // Brand Navy
                                        plotOptions: { bar: { columnWidth: '40%', borderRadius: 4 } },
                                        legend: { show: false }
                                    }}
                                    series={[
                                        { name: 'Sales', data: monthlyData.map(d => d.sales) }
                                    ]}
                                    type="bar"
                                    height={264}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-4">
                    <div className="card nd-card h-100 radius-12">
                        <div className="card-header border-bottom bg-base py-16 px-24">
                            <h6 className="text-lg fw-semibold mb-0">Order Status</h6>
                        </div>
                        <div className="card-body p-24 d-flex flex-column justify-content-center">
                            {orderStatusCount.length > 0 ? (
                                <>
                                    <div className="text-center mb-24">
                                        <h4 className="mb-0 fw-bold">{totalOrder || 0}</h4>
                                        <p className="text-sm text-secondary-light mb-0">Total Orders</p>
                                    </div>
                                    <div className="d-flex flex-column gap-3">
                                        {orderStatusCount.map((s, i) => (
                                            <div key={i}>
                                                <div className="d-flex align-items-center justify-content-between mb-1">
                                                    <span className="text-sm fw-medium text-secondary-light">{s.status}</span>
                                                    <span className="text-sm fw-semibold text-primary-light">{s.count.toLocaleString()}</span>
                                                </div>
                                                <div className="progress w-100 bg-primary-50" style={{ height: '6px' }}>
                                                    <div className="progress-bar bg-primary-600" role="progressbar" style={{ width: `${s.percentage}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <span className="text-secondary-light text-sm">No Orders Found</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── 3 · CATEGORY + STOCK ── */}
            <div className="row gy-4 mb-24">
                <div className="col-xl-6">
                    <div className="card nd-card h-100 radius-12">
                        <div className="card-header border-bottom bg-base py-16 px-24">
                            <h6 className="text-lg fw-semibold mb-0">Revenue by Category</h6>
                        </div>
                        <div className="card-body p-0">
                            <div className="d-flex flex-column gap-24" style={{ height: '350px', overflowY: 'auto', padding: '24px' }}>
                                {categoryByRevenue?.categories?.length > 0 ? (
                                    categoryByRevenue.categories.map((c, i) => (
                                        <div key={i}>
                                            <div className="d-flex align-items-center justify-content-between mb-8">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="w-12-px h-12-px rounded-circle bg-warning-main" />
                                                    <span className="text-sm fw-medium text-secondary-light">{c.categoryName}</span>
                                                </div>
                                                <span className="text-sm fw-bold text-primary-light">{formatCurrency(c.revenue, categoryByRevenue.currencyDetails)}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="progress w-100 bg-primary-50" style={{ height: '6px' }}>
                                                    <div className="progress-bar bg-primary-600" role="progressbar" style={{ width: `${c.revenuePercentage}%` }} />
                                                </div>
                                                <span className="text-xs text-secondary-light fw-medium">{c.revenuePercentage}%</span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center">
                                        <span className="text-sm text-secondary-light">No data available</span>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>

                {/* 
                <div className="col-xl-6">
                    <div className="card nd-card h-100 radius-12">
                        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                            <h6 className="text-lg fw-semibold mb-0">Low Stock Alerts</h6>
                            <span className="badge bg-warning-focus text-warning-main">{lowStock.length} Items</span>
                        </div>
                        <div className="card-body p-0">
                            <div className="d-flex flex-column gap-16" style={{ height: '350px', overflowY: 'auto', padding: '24px' }}>
                                {lowStock.map((item, i) => {
                                    const stockPct = Math.min(Math.round((item.stock / item.lowStockQuantity) * 100), 100);
                                    return (
                                        <div key={i} className="p-12 radius-8 border border-dashed border-secondary-light">
                                            <div className="d-flex align-items-center justify-content-between mb-8">
                                                <div>
                                                    <h6 className="text-sm fw-semibold mb-0">{item.productName}</h6>
                                                    <span className="text-xs text-secondary-light">{item.sku}</span>
                                                </div>
                                                <div className="text-end">
                                                    <h6 className="text-sm fw-bold mb-0 text-primary-light">{item.stock} / {item.lowStockQuantity}</h6>
                                                    <span className="text-xs text-secondary-light">units</span>
                                                </div>
                                            </div>
                                            <div className="progress w-100 bg-primary-50" style={{ height: '6px' }}>
                                                <div className="progress-bar bg-primary-600" role="progressbar" style={{ width: `${stockPct}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div> */}

                {/* ── 6 · CURRENCY DISTRIBUTION (LIVE) ── */}
                {/* <div className="row gy-4 mb-24"> */}
                <div className="col-xl-6">
                    <div className="card nd-card hover-shadow-lg transition-all" style={{ cursor: 'pointer' }}>
                        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                            <h6 className="text-lg fw-semibold mb-0">Currency Order Distribution</h6>
                            <span className="text-secondary-light text-sm italic">Overall View ({overviewPeriod})</span>
                        </div>
                        <div className="card-body p-24">
                            <ReactApexChart
                                options={{
                                    chart: { type: 'donut', toolbar: { show: false } },
                                    labels: ordersByCurrency.map(i => i.code),
                                    colors: AV_COLORS,
                                    dataLabels: { enabled: true, dropShadow: { enabled: false } },
                                    plotOptions: {
                                        pie: {
                                            donut: {
                                                size: '70%',
                                                labels: {
                                                    show: true,
                                                    total: {
                                                        show: true,
                                                        label: 'Total Orders',
                                                        formatter: () => ordersByCurrency.reduce((a, b) => a + (b.orders || 0), 0)
                                                    }
                                                }
                                            }
                                        }
                                    },
                                    legend: { position: 'bottom', markers: { radius: 12 } },
                                    tooltip: { enabled: true, theme: 'light' },
                                    responsive: [{ breakpoint: 480, options: { chart: { height: 300 }, legend: { position: 'bottom' } } }]
                                }}
                                series={ordersByCurrency.map(i => i.orders)}
                                type="donut"
                                height={380}
                            />
                        </div>
                    </div>
                </div>
                {/* </div> */}
            </div>

            {/* ── 4 · TOP SELLING + CUSTOMERS ── */}
            <div className="row gy-4 mb-24">
                <div className="col-xl-6">
                    <div className="card nd-card h-100 radius-12">
                        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                            <h6 className="text-lg fw-semibold mb-0">Top Selling Products</h6>
                        </div>
                        <div className="card-body p-24">
                            <div className="table-responsive">
                                <table className="table border-0 mb-0 nd-table-header-dark">
                                    <thead>
                                        <tr>
                                            <th className="border-0 text-sm fw-semibold">Product</th>
                                            <th className="border-0 text-sm fw-semibold">Sold</th>
                                            <th className="border-0 text-sm fw-semibold text-end">Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topSellingProducts?.data?.length === 0 ? (
                                            <tr><td colSpan={3} className="text-center py-4 text-secondary-light text-sm">No data available</td></tr>
                                        ) : topSellingProducts.data.map((p, idx) => (
                                            <tr key={idx}>
                                                <td className="ps-0 border-0">
                                                    <div className="d-flex align-items-center gap-4">
                                                        <div className="popular-product-img p-2" style={{ minWidth: 100 }}>
                                                            <img
                                                                src={p.productImage && p.productImage[0] ? `${IMAGE_BASE_URL}/${p.productImage[0].docPath}/${p.productImage[0].docName}` : '/assets/images/admin_profile.png'}
                                                                alt={p.productName}
                                                                style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 25 }}
                                                            />
                                                        </div>

                                                        <div>
                                                            <h6 className="text-sm fw-semibold mb-1 line-clamp-1">{p.productName}</h6>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="align-middle border-0 text-secondary-light text-sm fw-medium">{p.totalQuantitySold || 0}</td>
                                                <td className="align-middle border-0 text-end pe-0 fw-bold text-primary-light">{formatCurrency(p.totalPriceSold, topSellingProducts.currencyDetails)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-6">
                    <div className="card nd-card h-100 radius-12">
                        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                            <h6 className="text-lg fw-semibold mb-0">Top 5 Customers</h6>
                        </div>
                        <div className="card-body p-24">
                            <div className="table-responsive">
                                <table className="table border-0 mb-0 nd-table-header-dark">
                                    <thead>
                                        <tr>
                                            <th className="border-0 text-sm fw-semibold">Customer</th>
                                            <th className="border-0 text-sm fw-semibold">Orders</th>
                                            <th className="border-0 text-sm fw-semibold text-end">Spent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topCustomers?.topCustomers?.length === 0 && (
                                            <tr>
                                                <td colSpan={3} className="text-center">No Data Found</td>
                                            </tr>
                                        )}
                                        {topCustomers?.topCustomers?.map((c, i) => (
                                            <tr key={i}>
                                                <td className="ps-0 border-0">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div>
                                                            <h6 className="text-sm fw-semibold mb-0">{c.name}</h6>
                                                            {/* <span className="badge bg-success-focus text-success-main text-xxs">{c.trend}</span> */}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="align-middle border-0">
                                                    <span className="badge bg-primary-focus text-primary-main radius-4 px-8">{c.orderCount}</span>
                                                </td>
                                                <td className="align-middle border-0 text-end pe-0 fw-bold text-primary-light">{formatCurrency(c.totalSpent, topCustomers.currencyDetails)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── 5 · RECENT CUSTOMERS + INVOICES ── */}
            <div className="row gy-4 mb-24">
                <div className="col-xl-4">
                    <div className="card nd-card h-100 radius-12">
                        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                            <h6 className="text-lg fw-semibold mb-0">Recent Customers</h6>
                            {/* <button className="text-primary-600 fw-semibold text-sm bg-transparent border-0">View All</button> */}
                        </div>
                        <div className="card-body p-0">
                            <div className="d-flex flex-column gap-16" style={{ height: '350px', overflowY: 'auto', padding: '24px' }}>
                                {newCustomer.length === 0 ? (
                                    <div className="text-center py-4 text-secondary-light text-sm">No recent customers</div>
                                ) : newCustomer.map((c, i) => (
                                    <div key={i} className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-3">
                                            {/* <div className="w-40-px h-40-px radius-circle d-flex align-items-center justify-content-center text-white fw-bold text-xs"
                                                style={{ backgroundColor: AV_COLORS[i % AV_COLORS.length] }}>
                                                {initials(c.name)}
                                            </div> */}
                                            <div>
                                                <h6 className="text-sm fw-semibold mb-0 line-clamp-1">{c.name}</h6>
                                                <span className="text-xs text-secondary-light">{c.email || c.phone}</span>
                                            </div>
                                        </div>
                                        {/* <div className="w-8-px h-8-px rounded-circle bg-success-main" title="Active" /> */}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-8">
                    <div className="card nd-card h-100 radius-12">
                        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                            <h6 className="text-lg fw-semibold mb-0">Recent Invoices</h6>
                            {/* <button className="text-primary-600 fw-semibold text-sm bg-transparent border-0">View All</button> */}
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive" style={{ height: '350px', overflowY: 'auto', padding: '24px' }}>
                                <table className="table table-striped table-centered align-middle table-nowrap mb-0 nd-table-header-dark">
                                    <thead>
                                        <tr>
                                            <th className="border-0 text-sm fw-semibold">Order ID</th>
                                            <th className="border-0 text-sm fw-semibold">Customer</th>
                                            <th className="border-0 text-sm fw-semibold">Date</th>
                                            <th className="border-0 text-sm fw-semibold">Status</th>
                                            <th className="border-0 text-sm fw-semibold text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.length === 0 ? (
                                            <tr><td colSpan={5} className="text-center py-4 text-secondary-light text-sm">No data found</td></tr>
                                        ) : invoices.map((inv, i) => (
                                            <tr key={i}>
                                                <td className="ps-0 border-0 align-middle text-primary-600 text-sm fw-semibold">#{inv.id}</td>
                                                <td className="border-0 align-middle">
                                                    <div className="d-flex align-items-center">
                                                        <img
                                                            src={inv.avatar}
                                                            alt={inv.name}
                                                            className="rounded-circle avatar-sm"
                                                        />
                                                        <div className="ms-3">{inv.name}</div>
                                                    </div>
                                                </td>
                                                <td className="border-0 align-middle text-secondary-light text-sm">{inv.date}</td>
                                                <td>
                                                    <div
                                                        className={`badge font-size-12 ${inv.status === "Paid"
                                                            ? "bg-success-subtle text-success"
                                                            : "bg-danger-subtle text-danger"
                                                            }`}
                                                    >
                                                        {inv.status}
                                                    </div>
                                                </td>
                                                <td className="border-0 align-middle text-end pe-0">
                                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                                        <button className="w-32-px h-32-px radius-circle bg-primary-focus text-primary-main border-0 d-flex align-items-center justify-content-center" title="View">
                                                            <Icon icon="lucide:eye" className="text-sm" />
                                                        </button>
                                                        <button className="w-32-px h-32-px radius-circle bg-success-focus text-success-main border-0 d-flex align-items-center justify-content-center" title="Print">
                                                            <Icon icon="lucide:printer" className="text-sm" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



        </div>
    );
}